import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, FileText, Users, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const TermsOfService = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent">
              Terms of Service
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Please read these terms carefully before using FlameLink. By using our service, you agree to these terms.
          </p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: December 15, 2024 | Last Updated: December 15, 2024</p>
        </div>

        <div className="space-y-8">
          {/* Key Points */}
          <Card className="p-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 text-purple-500 mr-3" />
              Key Points
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Free to Join</h3>
                    <p className="text-gray-600 text-sm">Basic features are free with optional premium upgrades</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Users className="w-5 h-5 text-blue-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Community Guidelines</h3>
                    <p className="text-gray-600 text-sm">Respectful behavior is required from all members</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Safe Environment</h3>
                    <p className="text-gray-600 text-sm">We actively moderate and maintain platform safety</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-orange-500 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Age Requirement</h3>
                    <p className="text-gray-600 text-sm">Must be 18 years or older to use FlameLink</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Acceptance of Terms */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Acceptance of Terms</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Agreement to Terms:</strong> By accessing, downloading, or using FlameLink ("Service", "App", "Platform"), 
                you agree to be bound by these Terms of Service ("Terms") and all applicable laws and regulations.
              </p>
              <p>
                <strong>Legal Capacity:</strong> You must be at least 18 years old and have the legal capacity to enter into 
                binding agreements to use FlameLink.
              </p>
              <p>
                <strong>Updates to Terms:</strong> We reserve the right to update these Terms at any time. Material changes 
                will be notified through the app or email. Continued use after changes constitutes acceptance.
              </p>
              <p>
                <strong>Rejection of Terms:</strong> If you do not agree with any part of these Terms, you must not use FlameLink.
              </p>
            </div>
          </Card>

          {/* Eligibility and Registration */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Eligibility and Registration</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Age and Identity Requirements</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• You must be at least 18 years of age</li>
                  <li>• You must provide accurate and truthful personal information</li>
                  <li>• You must verify your identity through our verification process</li>
                  <li>• You must not have been previously banned from FlameLink</li>
                  <li>• You must comply with all applicable laws in your jurisdiction</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Account Requirements</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Maintain only one active account per person</li>
                  <li>• Keep your login credentials secure and confidential</li>
                  <li>• Notify us immediately of any unauthorized access</li>
                  <li>• Take responsibility for all activities under your account</li>
                  <li>• Provide and maintain current contact information</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.3 Geographic Restrictions</h3>
                <p className="text-gray-700">
                  FlameLink is available in select cities and regions. Service availability may be limited based on 
                  local laws, regulations, or business decisions. We reserve the right to restrict access in certain jurisdictions.
                </p>
              </div>
            </div>
          </Card>

          {/* User Conduct and Content */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. User Conduct and Content</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 text-green-600">3.1 Acceptable Use ✓</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Treat all users with respect, kindness, and dignity</li>
                  <li>• Use current, accurate, and unedited photos of yourself</li>
                  <li>• Engage in genuine, authentic conversations and interactions</li>
                  <li>• Report inappropriate behavior or content immediately</li>
                  <li>• Respect other users' boundaries and decisions</li>
                  <li>• Use the tipping feature responsibly and as intended</li>
                  <li>• Follow all community guidelines and safety recommendations</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 text-red-600">3.2 Prohibited Activities ✗</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Profile and Content Violations:</h4>
                    <ul className="space-y-1 text-gray-700 ml-4 text-sm">
                      <li>• Creating fake, misleading, or impersonation profiles</li>
                      <li>• Using photos of other people or copyrighted images</li>
                      <li>• Posting nude, sexually explicit, or inappropriate content</li>
                      <li>• Sharing content that promotes violence, hate speech, or discrimination</li>
                      <li>• Advertising commercial products or services</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Communication Violations:</h4>
                    <ul className="space-y-1 text-gray-700 ml-4 text-sm">
                      <li>• Harassment, bullying, stalking, or threatening behavior</li>
                      <li>• Sending unsolicited sexual content or inappropriate messages</li>
                      <li>• Spam, mass messaging, or automated communication</li>
                      <li>• Requesting money, financial information, or personal details</li>
                      <li>• Sharing contact information outside the platform prematurely</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Platform Abuse:</h4>
                    <ul className="space-y-1 text-gray-700 ml-4 text-sm">
                      <li>• Attempting to circumvent security measures or access restrictions</li>
                      <li>• Using bots, scripts, or automated systems</li>
                      <li>• Reverse engineering, copying, or modifying the app</li>
                      <li>• Creating multiple accounts or circumventing bans</li>
                      <li>• Interfering with other users' experience or platform operations</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Legal Violations:</h4>
                    <ul className="space-y-1 text-gray-700 ml-4 text-sm">
                      <li>• Any illegal activity or content under applicable laws</li>
                      <li>• Violating intellectual property rights</li>
                      <li>• Fraud, scams, or deceptive practices</li>
                      <li>• Drug sales, weapons trading, or other illegal commerce</li>
                      <li>• Tax evasion related to tip transactions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Tipping System Terms */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Tipping System</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Purpose:</strong> The tipping feature allows users to offer financial incentives ($1-$1000) 
                to encourage matches and demonstrate genuine interest.
              </p>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.1 How Tipping Works</h3>
                <ul className="space-y-2 ml-4">
                  <li>• Tips are offered when swiping right on profiles</li>
                  <li>• Recipients can see tip offers and decide whether to match</li>
                  <li>• Actual tip exchange occurs privately between matched users when they meet</li>
                  <li>• FlameLink does not process, facilitate, or guarantee tip payments</li>
                  <li>• Tips are voluntary and matches can occur without tip offers</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.2 Tip Limitations and Rules</h3>
                <ul className="space-y-2 ml-4">
                  <li>• Minimum tip offer: $1.00 USD</li>
                  <li>• Maximum tip offer: $1,000.00 USD</li>
                  <li>• Tips cannot be used to purchase sexual services or illegal activities</li>
                  <li>• False tip offers or failure to honor tips may result in account suspension</li>
                  <li>• Users are responsible for tax implications of tip transactions</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4.3 Platform Disclaimer</h3>
                <p>
                  FlameLink is not responsible for tip disputes, non-payment, or fraudulent tip offers. 
                  All tip arrangements are private agreements between users. Report problematic behavior 
                  through our reporting system.
                </p>
              </div>
            </div>
          </Card>

          {/* Paid Services */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Paid Services and "Tip Offers"</h2>
            <div className="space-y-4 text-gray-700">
              <p>Joining and using FlameLink's core features (profiles, discovery, matching, messaging) is free. FlameLink may offer an optional paid subscription, <strong>FlameLink Premium</strong>, which is not yet available for purchase. When offered, Premium is a <strong>recurring subscription</strong> (monthly, every 3 months, or yearly); the price and billing period are shown before you confirm, payments are processed by Stripe, and the subscription renews automatically until you cancel.</p>
              <p>You may cancel at any time from Settings. Cancellation stops future renewals and Premium features remain available until the end of the period already paid for. If a renewal payment cannot be collected, the subscription ends and Premium features are removed. Subscription payments are generally non-refundable except where required by law or as described in our Refund Policy, which forms part of these Terms.</p>
              <p>The "tip offer" feature lets you attach a symbolic amount to a like as a signal of interest. <strong>No money is charged, transferred, held or paid out</strong> in connection with tip offers. Accepting a tip offer only creates a match; it does not create any payment obligation between users.</p>
            </div>
          </Card>

          {/* Intellectual Property */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Intellectual Property</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.1 FlameLink's Rights</h3>
                <p>
                  All aspects of FlameLink, including but not limited to the app, website, logo, algorithms, 
                  matching technology, and user interface, are owned by FlameLink and protected by copyright, 
                  trademark, and other intellectual property laws.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.2 User Content Rights</h3>
                <ul className="space-y-2 ml-4">
                  <li>• You retain ownership of content you upload (photos, bio, messages)</li>
                  <li>• You grant FlameLink license to use your content for service provision</li>
                  <li>• You warrant that you own or have rights to all uploaded content</li>
                  <li>• You agree not to upload copyrighted material without permission</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.3 License Grant</h3>
                <p>
                  FlameLink grants you a limited, non-exclusive, non-transferable license to use the Service 
                  for personal, non-commercial purposes in accordance with these Terms.
                </p>
              </div>
            </div>
          </Card>

          {/* Privacy and Data Protection */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Privacy and Data Protection</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                Your privacy is governed by our Privacy Policy, which is incorporated into these Terms. 
                By using FlameLink, you agree to our data collection and use practices as described in the Privacy Policy.
              </p>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">7.1 Data Security</h3>
                <ul className="space-y-2 ml-4">
                  <li>• We implement industry-standard security measures</li>
                  <li>• Data is encrypted in transit (HTTPS) and passwords are stored only as salted hashes</li>
                  <li>• We regularly audit our security practices</li>
                  <li>• We notify users of any data breaches as required by law</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">7.2 International Data Transfers</h3>
                <p>
                  Your data may be transferred to and processed in countries other than your own. 
                  We ensure appropriate safeguards are in place for international transfers.
                </p>
              </div>
            </div>
          </Card>

          {/* Termination */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Account Termination</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">8.1 Termination by User</h3>
                <ul className="space-y-2 ml-4">
                  <li>• You may delete your account at any time through app settings</li>
                  <li>• Account deletion removes your profile from the platform</li>
                  <li>• Some data may be retained for legal and safety purposes</li>
                  <li>• Premium subscription benefits end immediately upon deletion</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">8.2 Termination by FlameLink</h3>
                <p>
                  We reserve the right to suspend, restrict, or terminate your account immediately 
                  for violations of these Terms, illegal activity, or behavior that harms other users 
                  or the platform.
                </p>
                
                <h4 className="font-medium text-gray-900 mt-4 mb-2">Reasons for Termination:</h4>
                <ul className="space-y-2 ml-4">
                  <li>• Violation of Terms of Service or Community Guidelines</li>
                  <li>• Fraudulent, abusive, or illegal behavior</li>
                  <li>• Multiple user reports of inappropriate conduct</li>
                  <li>• Attempts to circumvent platform security or restrictions</li>
                  <li>• Commercial use or spam activities</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">8.3 Effect of Termination</h3>
                <ul className="space-y-2 ml-4">
                  <li>• Your right to use FlameLink immediately ceases</li>
                  <li>• Your profile and content will be removed from public view</li>
                  <li>• No refunds will be provided for terminated accounts</li>
                  <li>• Certain data may be retained as required by law</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Disclaimers and Liability */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Disclaimers and Limitation of Liability</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">9.1 Service Disclaimers</h3>
                <ul className="space-y-2 ml-4">
                  <li>• FlameLink is provided "as is" without warranties of any kind</li>
                  <li>• We do not guarantee uninterrupted or error-free service</li>
                  <li>• We cannot guarantee the accuracy of user-provided information</li>
                  <li>• Matching algorithms are not guaranteed to find suitable partners</li>
                  <li>• We do not conduct criminal background checks on all users</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">9.2 User Interaction Disclaimer</h3>
                <p>
                  FlameLink is not responsible for user conduct, whether online or offline. Users interact at their own risk. 
                  We strongly recommend following safety guidelines and meeting in public places.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">9.3 Limitation of Liability</h3>
                <p>
                  To the fullest extent permitted by law, FlameLink's liability is limited to the amount paid 
                  by you for the service in the 12 months preceding the claim. We are not liable for indirect, 
                  incidental, consequential, or punitive damages.
                </p>
              </div>
            </div>
          </Card>

          {/* Governing Law */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Governing Law and Dispute Resolution</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">10.1 Governing Law</h3>
                <p>
                  These Terms are governed by the laws of Delaware, United States, without regard to conflict of law principles. 
                  Any legal action must be brought in the state or federal courts located in Delaware.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">10.2 Dispute Resolution Process</h3>
                <ol className="space-y-2 ml-4">
                  <li>1. <strong>Direct Resolution:</strong> Contact support@flamelink.com for informal resolution</li>
                  <li>2. <strong>Mediation:</strong> Good faith mediation attempt if direct resolution fails</li>
                  <li>3. <strong>Arbitration:</strong> Binding arbitration for remaining disputes (except small claims)</li>
                  <li>4. <strong>Class Action Waiver:</strong> No class actions or representative proceedings</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">10.3 Exceptions</h3>
                <p>
                  Either party may seek injunctive relief in court for intellectual property violations or 
                  urgent safety matters without prior arbitration.
                </p>
              </div>
            </div>
          </Card>

          {/* General Provisions */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">11. General Provisions</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">11.1 Entire Agreement</h3>
                <p>
                  These Terms, together with our Privacy Policy and Community Guidelines, constitute 
                  the entire agreement between you and FlameLink.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">11.2 Severability</h3>
                <p>
                  If any provision of these Terms is found invalid or unenforceable, the remaining 
                  provisions will continue in full force and effect.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">11.3 Assignment</h3>
                <p>
                  FlameLink may assign these Terms in connection with a merger, acquisition, or sale of assets. 
                  You may not assign your rights without our written consent.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">11.4 Force Majeure</h3>
                <p>
                  FlameLink is not liable for delays or failures due to circumstances beyond our reasonable control, 
                  including natural disasters, war, terrorism, or government actions.
                </p>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">12. Contact Information</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                For questions about these Terms of Service, please contact us:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Legal Department</h3>
                  <p>Email: legal@flamelink.com</p>
                  <p>Phone: +1 (555) 123-LEGAL</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Mailing Address</h3>
                  <p>
                    FlameLink Legal Department<br/>
                    123 Love Street, Suite 500<br/>
                    San Francisco, CA 94102<br/>
                    United States
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About These Terms?</h3>
            <p className="text-gray-600 mb-6">Contact our legal team for clarification on any terms</p>
            <Button
              onClick={() => window.open('mailto:legal@flamelink.com', '_blank')}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8 py-3 rounded-full"
            >
              Contact Legal Team
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;